import { CalendarClock, FileSignature, Wallet, type LucideIcon } from 'lucide-react';

import { parseSubjectParam } from '../dossier/subjectParam';

/**
 * advanceSteps.ts — the three areas an operational expense advance is set up in.
 *
 * `steps.ts` FOR A DIFFERENT SUBJECT, AND DELIBERATELY ITS MIRROR. The advance dossier is the
 * contract dossier's shape over one `loans` row: an area is a question, the manifest is the
 * preflight, and one function builds every path so a rename cannot half-land. What it is NOT is a
 * seventh setup area — `SetupArea` stays the closed union of six it has always been, and nothing
 * here widens it. The two vocabularies meet in exactly two places, both of them lookups rather than
 * unions: `labelForArea` consults `ADVANCE_AREA_LABELS` after its own, and `MERGE_FIELD_STEPS`
 * routes the `loan.` namespace to the keys below.
 *
 * THE ORDER IS THE SCHEDULE'S OWN. The loan v7 Schedule states the advance before it states how it
 * comes back, and both before the signatures, so an admin walking these three areas walks the
 * document they are about to generate from the top.
 *
 * PURE, AND REACT-FREE but for the icon type: vitest runs node-env over `.ts`, and the icons are
 * values the rail draws rather than anything this module executes.
 */
export type AdvanceArea = 'disbursement' | 'repayment' | 'agreement';

export interface AdvanceAreaConfig {
    key: AdvanceArea;
    label: string;
    /** The question the area answers, in the admin's own words. */
    question: string;
    icon: LucideIcon;
}

export const ADVANCE_AREAS: AdvanceAreaConfig[] = [
    {
        key: 'disbursement',
        label: 'Disbursement',
        question: 'What did Primelane pay out, what for, and at what rate?',
        icon: Wallet,
    },
    {
        key: 'repayment',
        label: 'Repayment',
        question: 'How is it repaid, and from when?',
        icon: CalendarClock,
    },
    {
        key: 'agreement',
        label: 'Agreement',
        question: 'Is the Schedule executed by both parties?',
        icon: FileSignature,
    },
];

export const ADVANCE_AREA_KEYS: AdvanceArea[] = ADVANCE_AREAS.map((area) => area.key);

export const ADVANCE_AREA_LABELS: Record<AdvanceArea, string> = ADVANCE_AREAS.reduce(
    (labels, area) => ({ ...labels, [area.key]: area.label }),
    {} as Record<AdvanceArea, string>,
);

/**
 * The area a URL fragment names, or null.
 *
 * NULL IS "NO AREA WAS NAMED", exactly as it is in `resolveArea`: an unknown value, a hand-edited
 * path or a missing segment all land on the same answer, and the page's landing rule takes it from
 * there rather than pinning the admin to the first area.
 */
export function resolveAdvanceArea(value: string | null | undefined): AdvanceArea | null {
    if (!value) return null;
    const key = value.toLowerCase();
    return ADVANCE_AREA_KEYS.includes(key as AdvanceArea) ? (key as AdvanceArea) : null;
}

/**
 * The `:loanId` segment as a subject — a loan id, the literal `new`, or nothing.
 *
 * THE RULE IS `subjectParam`'s AND THE NAME IS THIS DOSSIER'S. `loans.id` is a bigserial arriving
 * as a hand-editable URL segment, which is the same hazard every dossier route carries — so the
 * parser is shared and this stays the word the advance vocabulary calls it by. Its docblock has the
 * whole argument, `NaN` included.
 */
export function parseLoanParam(value: string | null | undefined): number | 'new' | null {
    return parseSubjectParam(value);
}

/**
 * The path an advance area lives at. ONE PLACE BUILDS IT, so a rename cannot half-land.
 *
 * `loan` IS THE SEGMENT AS IT IS WRITTEN, never a parse: `'new'` travels as the word the route
 * matches on, and an id travels as its digits. A bare area-less path is the landing route, which
 * the page replaces with a real area the moment it can resolve one.
 */
export function advanceAreaPath(
    contractId: number,
    loan: number | 'new',
    area: AdvanceArea | null,
): string {
    const base = `/dashboard/contracts/${contractId}/advances/${loan}`;
    return area ? `${base}/${area}` : base;
}
